import React from 'react'
import * as RadixTooltip from '@radix-ui/react-tooltip'
import { Box, SxStyleProp } from 'rebass/styled-components'

import theme from '../../../theme'

export interface TooltipProps {
  align?: 'start' | 'center' | 'end'
  children: React.ReactNode
  content?: string | React.ReactNode
  delay?: number
  disableTooltipHover?: boolean
  displayArrow?: boolean
  offset?: number
  onOpenChange?: (open: boolean) => void
  side?: 'top' | 'right' | 'bottom' | 'left'
  styles?: SxStyleProp
}

export const Tooltip = ({
  align = 'start',
  children,
  content,
  delay = 0,
  disableTooltipHover = false,
  displayArrow = false,
  offset = 5,
  onOpenChange,
  side = 'bottom'
}: TooltipProps): JSX.Element => {
  // Only render a tooltip if the content prop is provided
  if (!content) {
    return <>{children}</>
  }

  return (
    <RadixTooltip.Provider delayDuration={delay} disableHoverableContent={disableTooltipHover}>
      <RadixTooltip.Root onOpenChange={onOpenChange}>
        <RadixTooltip.Trigger asChild>
          {/* Tooltip trigger content must always be wrapped in a DOM element */}
          {typeof children === 'string' ? (
            <Box as="span" sx={{ cursor: 'pointer' }}>
              {children}
            </Box>
          ) : (
            children
          )}
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            align={align}
            side={side}
            sideOffset={offset}
            style={{ zIndex: theme.zIndices.tooltip }}
            onPointerDownOutside={event => event.preventDefault()}
          >
            <Box
              sx={{
                fontSize: 'var(--font-size-s)',
                maxWidth: '450px',
                p: 2,
                borderRadius: '3px',
                color: 'var(--tooltip-element-color)',
                border: '1px solid',
                borderColor: 'var(--tooltip-element-border)',
                backgroundColor: 'var(--tooltip-element-background)',
                boxShadow: 'popover'
              }}
            >
              {/* Tooltip content must always be wrapped in a DOM element */}
              {typeof content === 'string' ? <span>{content}</span> : content}
              {displayArrow && <RadixTooltip.Arrow width={14} height={7} fill="var(--tooltip-element-border)" />}
            </Box>
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  )
}
